import { useEffect, useState } from "react"

import type { EngagementDelivery } from "@ledgerbox/domain"
import { activityLine } from "./activity-lines"
import type { PortalApi } from "./api"
import { usePortalApi } from "./api-context"
import { formatBytes, formatDateTime, formatShortDate, plural } from "./format"
import { ActivitySkeleton, DeliverySkeleton, LoadingRegion } from "./skeleton"
import { CheckGlyph, FileGlyph, InlineError, RetryNotice } from "./ui"

type Deliverables = Awaited<ReturnType<PortalApi["listDeliverables"]>>
type Timeline = Awaited<ReturnType<PortalApi["getTimeline"]>>

/** Files shared on the same day read as one delivery, the way the firm sent
    them. Acknowledgment stays per file underneath. */
export const groupBySharedDay = (files: Deliverables["files"]) => {
  const groups = new Map<string, Deliverables["files"]>()
  for (const file of files) {
    const key = file.sharedAt.slice(0, 10)
    const group = groups.get(key)
    if (group) group.push(file)
    else groups.set(key, [file])
  }
  return [...groups.entries()].sort(([a], [b]) => (a < b ? 1 : -1))
}

export function PortalDeliverables({
  firmName,
  contactName,
}: {
  firmName: string
  /** Who is signed in: their own events read "You". */
  contactName: string
}) {
  const api = usePortalApi()
  const [files, setFiles] = useState<Deliverables["files"]>([])
  const [deliveries, setDeliveries] = useState<EngagementDelivery[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [acknowledging, setAcknowledging] = useState<string | null>(null)

  const loadDeliverables = async () => {
    setLoading(true)
    setError(null)
    try {
      const [legacy, engagement] = await Promise.all([
        api.listDeliverables(),
        api.listEngagementDeliveries(),
      ])
      setFiles(legacy.files)
      setDeliveries(engagement.deliveries)
    } catch {
      setError("Your shared files could not be loaded.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadDeliverables()
  }, [])

  const download = async (fileId: string) => {
    setError(null)
    try {
      const access = await api.getDeliverableAccess(fileId)
      window.open(access.accessUrl, "_blank", "noopener,noreferrer")
    } catch {
      setError("This file could not be opened. Try again.")
    }
  }

  const acknowledgeGroup = async (
    groupKey: string,
    groupFiles: Deliverables["files"]
  ) => {
    setError(null)
    setAcknowledging(groupKey)
    try {
      // In turn, not at once: each reply is the whole list as it stands, so
      // the last one is only the whole truth if the others are already in.
      let next: Deliverables | null = null
      for (const file of groupFiles.filter((each) => !each.acknowledged)) {
        next = await api.acknowledgeDeliverable(file.fileId)
      }
      if (next) setFiles(next.files)
    } catch {
      setError(`${firmName} could not be notified. Try again.`)
    } finally {
      setAcknowledging(null)
    }
  }

  const acknowledgeDelivery = async (deliveryId: string) => {
    setError(null)
    setAcknowledging(deliveryId)
    try {
      const next = await api.acknowledgeEngagementDelivery(deliveryId)
      setDeliveries(next.deliveries)
    } catch {
      setError(`${firmName} could not be notified. Try again.`)
    } finally {
      setAcknowledging(null)
    }
  }

  const downloadDeliveryFile = async (deliveryId: string, fileId: string) => {
    setError(null)
    try {
      const access = await api.getEngagementDeliveryAccess(deliveryId, fileId)
      window.open(access.accessUrl, "_blank", "noopener,noreferrer")
    } catch {
      setError("This file could not be opened. Try again.")
    }
  }

  const heading = (
    <div className="page-heading section-gap">
      <h1>For you</h1>
      <p className="lede">What {firmName} has sent back to you</p>
    </div>
  )

  if (loading)
    return (
      <div className="foryou">
        {heading}
        <LoadingRegion label="Loading shared files">
          <DeliverySkeleton />
        </LoadingRegion>
      </div>
    )
  if (error && !files.length && !deliveries.length) {
    return (
      <RetryNotice message={error} onRetry={() => void loadDeliverables()} />
    )
  }

  const groups = groupBySharedDay(files)

  return (
    <div className="foryou">
      {heading}
      {files.length === 0 && deliveries.length === 0 ? (
        <div className="empty-state view-empty">
          <strong>Nothing from {firmName} yet</strong>
          <span>
            Reports and finished files {firmName} shares with you show up here
          </span>
        </div>
      ) : (
        <>
          {deliveries.map((delivery) => {
            const acknowledged = delivery.acknowledgedByMe === true
            return (
              <article className="deliverable-card" key={delivery.deliveryId}>
                <h2>{delivery.title}</h2>
                <p className="meta">
                  {delivery.version > 1
                    ? `Version ${delivery.version} · shared ${formatShortDate(delivery.publishedAt)}`
                    : `Shared ${formatShortDate(delivery.publishedAt)}`}
                </p>
                {delivery.note ? (
                  <p className="delivery-note">{delivery.note}</p>
                ) : null}
                {delivery.files.map((file) => (
                  <div className="deliverable-file" key={file.fileId}>
                    <span className="file-icon" aria-hidden="true">
                      <FileGlyph />
                    </span>
                    <span className="file-name mono">{file.fileName}</span>
                    <span className="file-size">
                      {formatBytes(file.byteSize)}
                    </span>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label={`Open ${file.fileName}`}
                      disabled={!file.available}
                      onClick={() =>
                        void downloadDeliveryFile(
                          delivery.deliveryId,
                          file.fileId
                        )
                      }
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M12 4v11M7 11l5 5 5-5M5 20h14" />
                      </svg>
                    </button>
                  </div>
                ))}
                {acknowledged ? (
                  <div className="ack-done">
                    <CheckGlyph />
                    {firmName} knows you received this version
                  </div>
                ) : (
                  <button
                    type="button"
                    className="ack-btn"
                    disabled={acknowledging === delivery.deliveryId}
                    onClick={() =>
                      void acknowledgeDelivery(delivery.deliveryId)
                    }
                  >
                    <CheckGlyph />
                    {acknowledging === delivery.deliveryId
                      ? "Telling them…"
                      : `Tell ${firmName} I received this version`}
                  </button>
                )}
              </article>
            )
          })}
          {groups.map(([key, groupFiles]) => {
            const first = groupFiles[0]
            if (!first) return null
            const allAcknowledged = groupFiles.every(
              (file) => file.acknowledged
            )
            return (
              <article className="deliverable-card" key={key}>
                <h2>Shared {formatShortDate(first.sharedAt)}</h2>
                <p className="meta">
                  {groupFiles.length}{" "}
                  {plural(groupFiles.length, "file", "files")}
                </p>
                {groupFiles.map((file) => (
                  <div className="deliverable-file" key={file.fileId}>
                    <span className="file-icon" aria-hidden="true">
                      <FileGlyph />
                    </span>
                    <span className="file-name mono">{file.fileName}</span>
                    <span className="file-size">
                      {formatBytes(file.byteSize)}
                    </span>
                    <button
                      type="button"
                      className="icon-button"
                      aria-label={`Open ${file.fileName}`}
                      disabled={!file.available}
                      onClick={() => void download(file.fileId)}
                    >
                      <svg viewBox="0 0 24 24">
                        <path d="M12 4v11M7 11l5 5 5-5M5 20h14" />
                      </svg>
                    </button>
                  </div>
                ))}
                {allAcknowledged ? (
                  <div className="ack-done">
                    <CheckGlyph />
                    {firmName} knows you have{" "}
                    {plural(groupFiles.length, "it", "them")}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="ack-btn"
                    disabled={acknowledging === key}
                    onClick={() => void acknowledgeGroup(key, groupFiles)}
                  >
                    <CheckGlyph />
                    {acknowledging === key
                      ? "Telling them…"
                      : `Tell ${firmName} I have ${plural(groupFiles.length, "it", "them")}`}
                  </button>
                )}
              </article>
            )
          })}
        </>
      )}
      {error ? <InlineError message={error} /> : null}
      <ActivityFeed firmName={firmName} contactName={contactName} />
    </div>
  )
}

function ActivityFeed({
  firmName,
  contactName,
}: {
  firmName: string
  contactName: string
}) {
  const api = usePortalApi()
  const [timeline, setTimeline] = useState<Timeline | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadTimeline = async (cursor?: string) => {
    if (cursor) setLoadingMore(true)
    else setLoading(true)
    setError(null)
    try {
      const response = await api.getTimeline(cursor)
      setTimeline((current) => {
        if (!cursor || !current) return response
        const known = new Set(current.items.map((item) => item.id))
        return {
          ...response,
          items: [
            ...current.items,
            ...response.items.filter((item) => !known.has(item.id)),
          ],
        }
      })
    } catch {
      setError("Your activity could not be loaded.")
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    void loadTimeline()
  }, [])

  if (loading)
    return (
      <section aria-label="Recent activity">
        <h2 className="feed-title">Recent activity</h2>
        <LoadingRegion label="Loading activity">
          <ActivitySkeleton />
        </LoadingRegion>
      </section>
    )
  if (error && !timeline) {
    return <RetryNotice message={error} onRetry={() => void loadTimeline()} />
  }
  if (!timeline?.items.length)
    return (
      <section aria-label="Recent activity">
        <h2 className="feed-title">Recent activity</h2>
        <div className="empty-state is-compact">
          <strong>No activity yet</strong>
          <span>What you and {firmName} do in this portal shows up here</span>
        </div>
      </section>
    )

  return (
    <section aria-label="Recent activity">
      <h2 className="feed-title">Recent activity</h2>
      {timeline.items.map((item) => (
        // A hairline list in the client's words: no bullet, and the item a
        // line names is part of the line (R3-U12).
        <div className="fev" key={item.id}>
          <p>{activityLine(item, firmName, contactName)}</p>
          <p className="when">{formatDateTime(item.createdAt)}</p>
        </div>
      ))}
      {error ? <InlineError message={error} /> : null}
      {timeline.nextCursor ? (
        <button
          type="button"
          className="ghost-pill small timeline-more"
          disabled={loadingMore}
          aria-busy={loadingMore}
          onClick={() => void loadTimeline(timeline.nextCursor ?? undefined)}
        >
          {loadingMore ? "Loading" : "Load earlier activity"}
        </button>
      ) : null}
    </section>
  )
}
